import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import googleIcon from '../../assets/google_icon.svg';
import ForgotPasswordModal from './ForgotPasswordModal';
import { sendLoginOtp } from '../../services/authApi';

const FORM_ID = 'yovo-login-modal-form';

const LOGIN_FIELD_BG = 'rgba(232, 233, 237, 1)';
const LOGIN_FIELD_TEXT = '#20202A';

const loginTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
    backgroundColor: LOGIN_FIELD_BG,
    borderRadius: '8px',
    padding: '6px 10px',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: LOGIN_FIELD_TEXT,
    },
    '&.Mui-focused fieldset': {
      borderColor: LOGIN_FIELD_TEXT,
      borderWidth: '0.5px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 0',
      fontSize: { xs: '16px', sm: '16px', md: '14px' },
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#999',
    opacity: 1,
  },
};

const tabButtonSx = (active) => ({
  flex: 1,
  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
  fontSize: '14px',
  fontWeight: active ? 600 : 400,
  color: active ? '#20202A' : '#666',
  textTransform: 'none',
  borderBottom: active ? '2px solid #20202A' : '2px solid transparent',
  borderRadius: 0,
  py: 1,
  minHeight: '36px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#20202A',
  },
});

const mobileModeSegmentSx = (active) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
  fontSize: { xs: '14px', sm: '13px' },
  fontWeight: active ? 600 : 500,
  lineHeight: 1.2,
  color: active ? '#20202A' : '#666',
  textTransform: 'none',
  borderRadius: '10px',
  py: { xs: 1.25, sm: 1 },
  px: 1.5,
  minHeight: { xs: '44px', sm: '40px' },
  backgroundColor: active ? '#FFFFFF' : 'transparent',
  boxShadow: active ? '0 1px 4px rgba(32, 32, 42, 0.1)' : 'none',
  border: 'none',
  transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': {
    backgroundColor: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.55)',
    color: '#20202A',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: active ? '#20202A' : '#888',
    transition: 'color 0.2s ease',
  },
});

const LoginModal = ({ open, onClose, onSwitchToSignup }) => {
  const theme = useTheme();
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const { login, googleLogin, mobileOtpLogin, mobilePasswordLogin } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);

  // Tab: 'email' | 'mobile'
  const [activeTab, setActiveTab] = useState('email');
  // Mobile sub-mode: 'otp' | 'password'
  const [mobileMode, setMobileMode] = useState('otp');
  // OTP step: 'phone' | 'verify'
  const [otpStep, setOtpStep] = useState('phone');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    phone: '',
    otp: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [googleTermsAccepted, setGoogleTermsAccepted] = useState(false);
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setInterval(() => setOtpCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [otpCountdown]);

  const suppressNextLoginClickRef = useRef(false);
  const loginSubmitButtonRef = useRef(null);

  useEffect(() => {
    if (!open || !isCompactViewport) return undefined;

    let cleaned = false;
    let attachedNode = null;

    const onTouchEndNative = (e) => {
      if (loadingRef.current) return;
      const form = typeof document !== 'undefined' ? document.getElementById(FORM_ID) : null;
      if (!form) return;
      e.preventDefault();

      suppressNextLoginClickRef.current = true;
      window.setTimeout(() => {
        suppressNextLoginClickRef.current = false;
      }, 500);

      const active = document.activeElement;
      if (active && typeof active.blur === 'function') {
        active.blur();
      }

      form.requestSubmit();
    };

    const tryAttach = () => {
      const node = loginSubmitButtonRef.current;
      if (!node || cleaned || attachedNode) return;
      node.addEventListener('touchend', onTouchEndNative, { passive: false, capture: true });
      attachedNode = node;
    };

    tryAttach();
    const rafId = requestAnimationFrame(tryAttach);

    return () => {
      cleaned = true;
      cancelAnimationFrame(rafId);
      if (attachedNode) {
        attachedNode.removeEventListener('touchend', onTouchEndNative, true);
      }
    };
  }, [open, isCompactViewport]);

  const handleLoginClickCapture = useCallback((e) => {
    if (!suppressNextLoginClickRef.current) return;
    suppressNextLoginClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === 'phone') {
      value = value.replace(/[^\d+]/g, '');
      if (value.indexOf('+') > 0) value = value.replace(/\+/g, '');
    }
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (activeTab === 'email') {
        if (!formData.username || !formData.password) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }
        await login(formData.username, formData.password);
        resetAndClose();
      } else if (activeTab === 'mobile') {
        if (mobileMode === 'otp') {
          if (otpStep === 'phone') {
            if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
              setError('Enter a valid mobile number');
              setLoading(false);
              return;
            }
            await sendLoginOtp(formData.phone);
            setOtpStep('verify');
            setOtpCountdown(120);
          } else {
            if (!formData.otp || formData.otp.length !== 6) {
              setError('Enter the 6-digit OTP');
              setLoading(false);
              return;
            }
            const result = await mobileOtpLogin(formData.phone, formData.otp);
            if (result.needsSignup) {
              // New user — redirect to signup with verified phone
              handleClose();
              if (onSwitchToSignup) onSwitchToSignup(result.verifiedPhone);
              return;
            }
            resetAndClose();
          }
        } else {
          if (!formData.phone || !formData.password) {
            setError('Please fill in all fields');
            setLoading(false);
            return;
          }
          await mobilePasswordLogin(formData.phone, formData.password);
          resetAndClose();
        }
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCountdown > 0) return;
    setError('');
    try {
      await sendLoginOtp(formData.phone);
      setOtpCountdown(120);
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    }
  };

  const resetAndClose = () => {
    setFormData({ username: '', password: '', phone: '', otp: '' });
    setError('');
    setOtpStep('phone');
    setOtpCountdown(0);
    setGoogleTermsAccepted(false);
    onClose();
  };

  const handleClose = () => {
    setFormData({ username: '', password: '', phone: '', otp: '' });
    setError('');
    setOtpStep('phone');
    setOtpCountdown(0);
    setGoogleTermsAccepted(false);
    onClose();
  };

  const handleAuthFieldFocus = useCallback((e) => {
    const root = e?.target?.closest?.('.MuiTextField-root');
    if (!root) return;
    const run = () => {
      try {
        root.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
      } catch {
        root.scrollIntoView(true);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);

  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError('');
      setGoogleLoading(true);
      try {
        await googleLogin(tokenResponse.access_token);
        setFormData({ username: '', password: '', phone: '', otp: '' });
        onClose();
      } catch (err) {
        setError(err.message || 'Google login failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setError('Google login was cancelled or failed. Please try again.');
    },
  });

  const formatCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const renderEmailForm = () => (
    <>
      <Box>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: '#20202A',
            mb: 0.5
          }}
        >
          Registered Email
        </Typography>
        <TextField
          name="username"
          placeholder="Enter Email"
          type="email"
          value={formData.username}
          onChange={handleChange}
          onFocus={handleAuthFieldFocus}
          fullWidth
          required
          disabled={loading}
          autoComplete="username"
          inputProps={{ enterKeyHint: 'next' }}
          sx={loginTextFieldSx}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            color: '#20202A',
            mb: 0.5
          }}
        >
          Password
        </Typography>
        <TextField
          name="password"
          placeholder="Enter Password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          onFocus={handleAuthFieldFocus}
          fullWidth
          required
          disabled={loading}
          autoComplete="current-password"
          inputProps={{ enterKeyHint: 'done' }}
          sx={loginTextFieldSx}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              sx={{
                color: '#20202A',
                '&.Mui-checked': {
                  color: '#20202A'
                }
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '14px',
                color: '#20202A'
              }}
            >
              Remember me
            </Typography>
          }
        />
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setShowForgotPassword(true);
          }}
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            color: 'rgba(126, 128, 143, 1)',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          Forgot Password
        </Link>
      </Box>
    </>
  );

  const renderMobileForm = () => {
    if (mobileMode === 'otp') {
      return otpStep === 'phone' ? (
        <Box>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#20202A',
              mb: 0.5
            }}
          >
            Mobile Number
          </Typography>
          <TextField
            name="phone"
            placeholder="+91 9876543210"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            onFocus={handleAuthFieldFocus}
            fullWidth
            required
            disabled={loading}
            inputProps={{ maxLength: 15, enterKeyHint: 'done', inputMode: 'tel' }}
            sx={loginTextFieldSx}
          />
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '12px',
              color: '#999',
              mt: 0.5
            }}
          >
            We'll send a 6-digit OTP to this number
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#20202A',
              mb: 0.5
            }}
          >
            Enter OTP
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '12px',
              color: '#666',
              mb: 1
            }}
          >
            Sent to {formData.phone}
          </Typography>
          <TextField
            name="otp"
            placeholder="Enter 6-digit OTP"
            type="text"
            value={formData.otp}
            onChange={handleChange}
            onFocus={handleAuthFieldFocus}
            fullWidth
            required
            disabled={loading}
            inputProps={{ maxLength: 6, enterKeyHint: 'done', inputMode: 'numeric', style: { letterSpacing: '4px', textAlign: 'center' } }}
            sx={loginTextFieldSx}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setOtpStep('phone');
                setFormData(f => ({ ...f, otp: '' }));
                setError('');
              }}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '13px',
                color: '#666',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Change number
            </Link>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleResendOtp();
              }}
              sx={{
                fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                fontSize: '13px',
                color: otpCountdown > 0 ? '#999' : '#1976d2',
                textDecoration: 'none',
                pointerEvents: otpCountdown > 0 ? 'none' : 'auto',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {otpCountdown > 0 ? `Resend in ${formatCountdown(otpCountdown)}` : 'Resend OTP'}
            </Link>
          </Box>
        </Box>
      );
    }

    // Password mode
    return (
      <>
        <Box>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#20202A',
              mb: 0.5
            }}
          >
            Mobile Number
          </Typography>
          <TextField
            name="phone"
            placeholder="+91 9876543210"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            onFocus={handleAuthFieldFocus}
            fullWidth
            required
            disabled={loading}
            inputProps={{ maxLength: 15, enterKeyHint: 'next', inputMode: 'tel' }}
            sx={loginTextFieldSx}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: '#20202A',
              mb: 0.5
            }}
          >
            Password
          </Typography>
          <TextField
            name="password"
            placeholder="Enter Password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            onFocus={handleAuthFieldFocus}
            fullWidth
            required
            disabled={loading}
            autoComplete="current-password"
            inputProps={{ enterKeyHint: 'done' }}
            sx={loginTextFieldSx}
          />
        </Box>
      </>
    );
  };

  const getSubmitLabel = () => {
    if (activeTab === 'email') return 'Login';
    if (mobileMode === 'otp' && otpStep === 'phone') return 'Send OTP';
    if (mobileMode === 'otp' && otpStep === 'verify') return 'Verify & Login';
    return 'Login';
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        disableScrollLock={isCompactViewport}
        TransitionProps={{
          timeout: 0
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            width: {
              xs: '90vw',
              sm: '592px',
              md: '592px',
              lg: '592px'
            },
            maxWidth: '592px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'none !important',
            m: 2,
            maxHeight: {
              xs: 'min(calc(100dvh - 24px), calc(100% - 16px))',
              sm: 'min(calc(100dvh - 24px), calc(100% - 16px))',
              md: 'none',
            },
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              transition: 'opacity 0ms !important'
            }
          }
        }}
      >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 1,
          pt: 2,
          px: 3,
          flexDirection: 'column',
          gap: 0.5,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            padding: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: '20px', color: '#20202A' }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#20202A',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Login
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#666',
            textAlign: 'center',
            width: '100%'
          }}
        >
          Login to access your Yovotrip account
        </Typography>
      </DialogTitle>

      {/* Tab Switcher */}
      <Box sx={{ display: 'flex', mx: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Button
          disableRipple
          onClick={() => { setActiveTab('email'); setError(''); setOtpStep('phone'); }}
          sx={tabButtonSx(activeTab === 'email')}
        >
          Email
        </Button>
        <Button
          disableRipple
          onClick={() => { setActiveTab('mobile'); setError(''); }}
          sx={tabButtonSx(activeTab === 'mobile')}
        >
          Mobile
        </Button>
      </Box>

      {/* Mobile login method — segmented control (OTP default) */}
      {activeTab === 'mobile' && (
        <Box sx={{ mx: 3, mt: 2, mb: 0.25 }}>
          <Box
            role="group"
            aria-label="Mobile login method"
            sx={{
              display: 'flex',
              gap: '4px',
              p: '4px',
              borderRadius: '12px',
              backgroundColor: LOGIN_FIELD_BG,
            }}
          >
            <Button
              disableRipple
              aria-pressed={mobileMode === 'otp'}
              onClick={() => { setMobileMode('otp'); setError(''); setOtpStep('phone'); setFormData(f => ({ ...f, otp: '' })); }}
              sx={mobileModeSegmentSx(mobileMode === 'otp')}
            >
              <SmsOutlinedIcon aria-hidden />
              OTP
            </Button>
            <Button
              disableRipple
              aria-pressed={mobileMode === 'password'}
              onClick={() => { setMobileMode('password'); setError(''); setOtpStep('phone'); }}
              sx={mobileModeSegmentSx(mobileMode === 'password')}
            >
              <LockOutlinedIcon aria-hidden />
              Password
            </Button>
          </Box>
        </Box>
      )}

      <DialogContent
        sx={{
          px: 3,
          py: 1.5,
          pt: 1.5,
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        <Box
          component="form"
          id={FORM_ID}
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          {error && (
            <Alert severity="error" sx={{ fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif' }}>
              {error}
            </Alert>
          )}

          {activeTab === 'email' ? renderEmailForm() : renderMobileForm()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: { xs: 'max(16px, env(safe-area-inset-bottom, 0px))', md: 2 },
          pt: 1.5,
          flexDirection: 'column',
          gap: 1,
          borderTop: 'none',
          flexShrink: 0,
        }}
      >
        <Button
          ref={loginSubmitButtonRef}
          type="submit"
          form={FORM_ID}
          variant="contained"
          fullWidth
          disabled={loading}
          onMouseDown={(e) => {
            if (!isCompactViewport || loading) return;
            e.preventDefault();
          }}
          onClickCapture={handleLoginClickCapture}
          sx={{
            backgroundColor: '#20202A',
            color: '#FBFBFB',
            borderRadius: '18px',
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: 'Host Grotesk',
            minHeight: '44px',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': {
              backgroundColor: '#333'
            },
            '&:disabled': {
              backgroundColor: '#ccc'
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : getSubmitLabel()}
        </Button>
        
        {/* Passive policy notice for all login methods */}
        <Typography sx={{ fontSize: '11px', color: '#888', textAlign: 'center', mt: 0.5, px: 1, lineHeight: 1.4 }}>
          By continuing, you agree to YovoTrip's{' '}
          <RouterLink 
            to="/user-agreement" 
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1565C0', textDecoration: 'none' }}
          >
            User Agreement / Terms of Use
          </RouterLink>{' '}
          and acknowledge our{' '}
          <RouterLink 
            to="/privacy-policy" 
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1565C0', textDecoration: 'none' }}
          >
            Privacy Policy
          </RouterLink>
          .
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              color: '#666'
            }}
          >
            Don't have an account?
          </Typography>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleClose();
              if (onSwitchToSignup) onSwitchToSignup();
            }}
            sx={{
              fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
              fontSize: '14px',
              color: '#1976d2',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Sign up
          </Link>
        </Box>

        {activeTab === 'email' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1, pt: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
              <Typography
                sx={{
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  fontSize: '14px',
                  color: 'rgba(126, 128, 143, 1)',
                  fontWeight: 400,
                  whiteSpace: 'nowrap'
                }}
              >
                Or login with
              </Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            </Box>
            
            {/* Google consent checkbox - required because Google can auto-create accounts */}
            <Box sx={{ width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={googleTermsAccepted}
                    onChange={(e) => setGoogleTermsAccepted(e.target.checked)}
                    disabled={loading || googleLoading}
                    sx={{ 
                      color: '#20202A', 
                      '&.Mui-checked': { color: '#20202A' }, 
                      p: '2px 6px 2px 0',
                      alignSelf: 'flex-start',
                      mt: '-1px'
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontSize: '11px', color: '#666', lineHeight: 1.3 }}>
                    I agree to YovoTrip's{' '}
                    <RouterLink 
                      to="/user-agreement" 
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#1565C0', textDecoration: 'underline' }}
                    >
                      User Agreement / Terms of Use
                    </RouterLink>{' '}
                    and{' '}
                    <RouterLink 
                      to="/privacy-policy" 
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#1565C0', textDecoration: 'underline' }}
                    >
                      Privacy Policy
                    </RouterLink>
                    .
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, width: '100%', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (!googleTermsAccepted) {
                    setError('Please accept the terms to continue with Google.');
                    return;
                  }
                  triggerGoogleLogin();
                }}
                disabled={loading || googleLoading}
                sx={{
                  flex: 1,
                  maxWidth: '120px',
                  height: '44px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  color: '#20202A',
                  fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                  textTransform: 'none',
                  backgroundColor: 'rgba(232, 233, 237, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  '&:hover': {
                    borderColor: '#20202A',
                    backgroundColor: 'rgba(232, 233, 237, 0.8)'
                  },
                  '&:disabled': {
                    borderColor: '#e0e0e0',
                    backgroundColor: 'rgba(232, 233, 237, 0.5)'
                  }
                }}
              >
                {googleLoading ? (
                  <CircularProgress size={20} sx={{ color: '#20202A' }} />
                ) : (
                  <img src={googleIcon} alt="Google" style={{ width: '24px', height: '24px' }} />
                )}
              </Button>
            </Box>
          </Box>
        )}
      </DialogActions>
      </Dialog>

      <ForgotPasswordModal
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSwitchToLogin={() => {
          setShowForgotPassword(false);
        }}
      />
    </>
  );
};

export default LoginModal;
